/**
 * HR_FROZEN_FIELDS — Single source of truth for freeze logic.
 *
 * Each entry maps an interview_schedule column (scheduleKey) to its
 * corresponding profile-form field key (formKey).
 *
 * When a candidate was added by an HR (not by Sheela AI) and the
 * schedule record is less than 3 months old, every field listed here
 * is pre-filled from interview_schedule and rendered as readOnly.
 *
 * Fields NOT in this list fall back to candidateInfo / user_other_info
 * and are always editable.
 *
 * "step" indicates which registration step the field appears in:
 *   2 = Resume upload (ProfileVerify)
 *   3 = Mobile number (ProfileVerify)
 *   4 = Employment / skills (CompleteProfile)
 */
export const HR_FROZEN_FIELDS = [
  // Step 3 — ProfileVerify: phone number (never editable, separate verify flow)
  { scheduleKey: 'contact_no',      formKey: 'contact_no',      step: 3 },

  // Step 2 — ProfileVerify: resume
  { scheduleKey: 'resume_url',      formKey: 'resume_url',      step: 2 },

  // Step 4 — CompleteProfile: employment details
  { scheduleKey: 'current_company', formKey: 'current_company', step: 4 },
  { scheduleKey: 'job_status',      formKey: 'job_status',      step: 4 },
  { scheduleKey: 'notice_period',   formKey: 'notice_period',   step: 4 },
  { scheduleKey: 'current_ctc',     formKey: 'current_ctc',     step: 4 },
  { scheduleKey: 'experience',          formKey: 'total_experience',    step: 4 },
  { scheduleKey: 'is_job_role_aware',   formKey: 'jobtitle',           step: 4 },

  // Step 4 — CompleteProfile: new fields added to freeze scope
  { scheduleKey: 'relevant_experience', formKey: 'relevant_experience', step: 4 },
  { scheduleKey: 'expected_ctc',        formKey: 'expectedCtc',         step: 4 },
  { scheduleKey: 'reason_job_change',   formKey: 'reasonJobChange',     step: 4 },
  { scheduleKey: 'is_salary_negotiation', formKey: 'isSalaryNegotiable', step: 4 },
  { scheduleKey: 'can_notice_buyout',   formKey: 'canNoticeBuyout',     step: 4 },
  { scheduleKey: 'candidate_query',     formKey: 'candidateQuery',      step: 4 },
];

/**
 * Compute freeze status from schedule_info.
 * Returns { isFrozen, freezeUntilDate } or { isFrozen: false } when
 * no qualifying HR schedule exists.
 */
export const computeFreezeStatus = (scheduleInfo) => {
  if (!scheduleInfo) return { isFrozen: false, freezeUntilDate: null };

  const isHrAdded =
    scheduleInfo.added_by_email !== 'sheela@virtualemployee.com' &&
    scheduleInfo.added_by_name !== 'Sheela AI';

  if (!isHrAdded || !scheduleInfo.created_at) {
    return { isFrozen: false, freezeUntilDate: null };
  }

  const freezeUntil = new Date(scheduleInfo.created_at);
  freezeUntil.setMonth(freezeUntil.getMonth() + 3);

  return {
    isFrozen: new Date() < freezeUntil,
    freezeUntilDate: freezeUntil,
  };
};

/**
 * Build a Set of formKeys for quick lookups.
 */
export const FROZEN_FORM_KEYS = new Set(
  HR_FROZEN_FIELDS.map((f) => f.formKey)
);

/**
 * Build a scheduleKey → formKey map for quick lookups.
 */
export const SCHEDULE_TO_FORM_MAP = Object.fromEntries(
  HR_FROZEN_FIELDS.map((f) => [f.scheduleKey, f.formKey])
);
